import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import { supabase } from '@/lib/supabaseClient';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useProducts } from '@/contexts/ProductsContext';
import type { Article, BodyBlock } from '@/types/article';
import { ARROW, ARTICLE_FIELDS, CategoryTag, formatDate, initials, useReveal } from './shared';
import '../../home/v2/home-v2.css';
import './content-v2.css';

// New-design article page (/content/:slug): reading progress bar, a sticky
// contents list built from the article's headings, editorial typography,
// share buttons, related articles and the 3-Step call. The SEO tags and the
// not-found case stay in the parent page (ArticleDetailPage).

const slugify = (s: string, i: number) =>
  `s${i}-` +
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

function bold(text: string) {
  return text.split(/(\*\*.*?\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>));
}

function Block({ block, id, first }: { block: BodyBlock; id: string; first: boolean }) {
  switch (block.type) {
    case 'paragraph':
      return <p className={first ? 'lead' : undefined}>{bold(block.content)}</p>;
    case 'heading':
      return <h2 id={id}>{block.content}</h2>;
    case 'subheading':
      return <h3>{block.content}</h3>;
    case 'list':
      return (
        <ul>
          {block.items.map((it, i) => (
            <li key={i}>{bold(it)}</li>
          ))}
        </ul>
      );
    case 'blockquote':
      return (
        <blockquote>
          <p>{block.content}</p>
        </blockquote>
      );
    default:
      return null;
  }
}

export default function ArticleV2({ article }: { article: Article }) {
  const { t } = useTranslation();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { routineSet } = useProducts();
  const [others, setOthers] = useState<Article[]>([]);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState('');
  const [copied, setCopied] = useState(false);
  const root = useReveal<HTMLDivElement>();

  const toc = useMemo(
    () => article.body.map((b, i) => (b.type === 'heading' ? { id: slugify(b.content, i), text: b.content } : null)).filter((x): x is { id: string; text: string } => !!x),
    [article],
  );
  const firstPara = article.body.findIndex((b) => b.type === 'paragraph');

  useEffect(() => {
    window.scrollTo(0, 0);
    supabase
      .from('rxk_articles')
      .select(ARTICLE_FIELDS)
      .eq('status', 'published')
      .neq('slug', article.slug)
      .order('published_date', { ascending: false })
      .then(({ data }) => setOthers((data as Article[]) ?? []));
  }, [article.slug]);

  // Reading progress across the article body.
  useEffect(() => {
    const body = document.getElementById('art-body');
    const on = () => {
      if (!body) return;
      const r = body.getBoundingClientRect();
      const total = r.height - window.innerHeight * 0.6;
      setProgress(Math.min(1, Math.max(0, -r.top / (total || 1))));
    };
    on();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('scroll', on);
      window.removeEventListener('resize', on);
    };
  }, [article.slug]);

  // Highlight the section being read in the contents list.
  useEffect(() => {
    if (!toc.length) return;
    const io = new IntersectionObserver(
      (es) => {
        const vis = es.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (vis) setActive(vis.target.id);
      },
      { rootMargin: '-15% 0px -70% 0px' },
    );
    toc.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [toc]);

  const related = [...others.filter((o) => o.category === article.category), ...others.filter((o) => o.category !== article.category)].slice(0, 3);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const share = (
    <div className="ashare">
      <span>{t('content.v2.share', 'Share')}</span>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" aria-label="Facebook">
        <i className="ri-facebook-fill" />
      </a>
      <a href={`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + url)}`} target="_blank" rel="noreferrer" aria-label="WhatsApp">
        <i className="ri-whatsapp-line" />
      </a>
      <button type="button" onClick={copy} aria-label={t('content.v2.copy', 'Copy link')} className={copied ? 'ok' : ''}>
        <i className={copied ? 'ri-check-line' : 'ri-link'} />
      </button>
    </div>
  );

  let hi = 0;
  return (
    <>
      <SharedNavbar />
      <div className="aprog" style={{ transform: `scaleX(${progress})` }} aria-hidden />
      <main className="w-full min-h-screen bg-white">
        <div ref={root} className="v2 cnt art" style={{ paddingTop: 'var(--v2h-h, 64px)' }}>
          {/* Head */}
          <header className="ahead">
            <div className="wrap">
              <nav className="acrumb rv" aria-label="Breadcrumb">
                <Link to={prefix + '/content'}>{t('content.hero.heading')}</Link>
                <span>/</span>
                <span>{article.category}</span>
              </nav>
              <div className="rv" style={{ ['--d' as string]: '.05s' }}>
                <CategoryTag article={article} />
              </div>
              <h1 className="rv" style={{ ['--d' as string]: '.1s' }}>
                {article.title}
              </h1>
              {article.excerpt && (
                <p className="aexc rv" style={{ ['--d' as string]: '.15s' }}>
                  {article.excerpt}
                </p>
              )}
              <div className="aby rv" style={{ ['--d' as string]: '.2s' }}>
                <span className="aav">{initials(article.author)}</span>
                <div>
                  <b>{article.author || 'Rx.K Seoul'}</b>
                  <small>
                    {article.author_role && `${article.author_role} · `}
                    {formatDate(article.published_date, currentLang)}
                    {article.read_time && ` · ${article.read_time}`}
                  </small>
                </div>
              </div>
            </div>
          </header>

          {article.image && (
            <figure className="ahero rv">
              <img src={article.image} alt={article.title} />
            </figure>
          )}

          {/* Body */}
          <section className="abody">
            <div className={`wrap alay${toc.length > 1 ? ' has-toc' : ''}`}>
              {toc.length > 1 && (
                <aside className="atoc">
                  <span className="lbl">{t('content.v2.contents', 'In this article')}</span>
                  <ol>
                    {toc.map((h, i) => (
                      <li key={h.id} className={active === h.id ? 'on' : ''}>
                        <a
                          href={'#' + h.id}
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                        >
                          <em>{String(i + 1).padStart(2, '0')}</em>
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                  {share}
                </aside>
              )}
              <article id="art-body" className="aprose">
                {article.body.map((b, i) => {
                  const id = b.type === 'heading' ? (toc[hi++]?.id ?? '') : '';
                  return <Block key={i} block={b} id={id} first={i === firstPara} />;
                })}
                <div className="afoot">
                  <Link to={prefix + '/content'} className="aback">
                    <svg viewBox="0 0 24 24">
                      <path d={ARROW} />
                    </svg>
                    {t('content.allArticles')}
                  </Link>
                  {share}
                </div>
              </article>
            </div>
          </section>

          {/* 3-Step call */}
          <section className="acta">
            <div className="wrap">
              <div className="acard rv">
                <div className="acimg">
                  <img src={routineSet?.image || '/v2/giftbox.webp'} alt="" loading="lazy" />
                </div>
                <div className="actx">
                  <span className="lbl">{t('content.v2.ctaLabel', 'Your next step')}</span>
                  <h2>{t('content.v2.ctaTitle', 'Ready to start your skincare journey?')}</h2>
                  <p>{t('content.v2.ctaText', "Discover the 3-Step Daily Treatment designed for Bangladesh's climate.")}</p>
                  <div className="cbtns">
                    <Link to={prefix + '/3-step-treatment'} className="btn dark">
                      {t('content.v2.bandSet', 'Discover the 3-Step')}
                    </Link>
                    <Link to={prefix + '/products'} className="btn ghost">
                      {t('content.v2.bandShop', 'Shop products')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section className="arel">
              <div className="wrap">
                <div className="arelh rv">
                  <h2>{t('content.relatedArticles')}</h2>
                  <Link to={prefix + '/content'} className="cmore">
                    {t('content.allArticles')}
                    <svg viewBox="0 0 24 24">
                      <path d={ARROW} />
                    </svg>
                  </Link>
                </div>
                <div className="cgrid">
                  {related.map((a, i) => (
                    <Link key={a.slug} to={`${prefix}/content/${a.slug}`} className="ccard rv" style={{ ['--d' as string]: `${i * 0.08}s` }}>
                      <div className="cimg">{a.image && <img src={a.image} alt={a.title} loading="lazy" />}</div>
                      <CategoryTag article={a} />
                      <h3>{a.title}</h3>
                      <div className="cmeta">
                        <span>{formatDate(a.published_date, currentLang)}</span>
                        {a.read_time && <span>{a.read_time}</span>}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
        <SharedFooter />
      </main>
    </>
  );
}
